import { useStyles } from '@archipro-design/aria';
import React, { useEffect } from 'react';
import { useProductTagging } from '../../context';
import * as S from './ProductTaggingShell.style';
import type {
    OptimisticTags,
    TagProductType,
    TagType,
} from '@modules/product-tagging/type';
import TagWithModals from '../tag-with-modals/TagWithModals';
import { getTagPosition } from '../../util/get-tag-position';
import ProductTaggingNewTag from '../product-tagging-new-tag/ProductTaggingNewTag';
import { getPercentageValue, roundToTwo } from '~/utils/math';
import useDeleteTag from '../../hooks/use-delete-tag';
import useOptimisticTags from '../../hooks/use-optimistic-tags';
import ProductTaggingClickableArea from '../product-tagging-clickable-area/ProductTaggingClickableArea';
import useCreateTag from '../../hooks/use-create-tag';
import type { Image } from '~/modules/page-builder/type/message-passing';
import { useClickAway } from 'ahooks';
import usePendingFetcherQueue from '@modules/root/hook/use-pending-fetcher-queue';
import { toast } from 'sonner';

interface ProductTaggingShellProps {
    libraryImage: Image;
    tags?: TagType[];
    onTagCreatedOrUpdated?: (tagId?: number, approved?: boolean) => void;
    onTagDeleted?: (tagId: number) => void;
}

const PRODUCT_TAGGING_CLICKABLE_AREA_CLASS = 'product-tagging-shell';

const ProductTaggingShell = ({
    libraryImage,
    tags,
    onTagCreatedOrUpdated,
    onTagDeleted,
}: ProductTaggingShellProps) => {
    const {
        state: { isTagging, tagPosition },
        dispatch,
        canDeleteTag,
    } = useProductTagging();
    const ref = React.useRef<HTMLDivElement>(null);
    const { css } = useStyles();
    const onClickClickableArea = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        // reset isAddingSupplier & lastAddedTag when creating new tag
        dispatch({ type: 'RESET_TAGGING_FLAGS' });

        const rect = e.currentTarget.getBoundingClientRect();

        // The position used is the top left corner of the tag
        const leftPos = e.clientX - rect.left;
        const topPos = e.clientY - rect.top;

        // Convert the pixel positioning to percentages
        const pctX = getPercentageValue(leftPos, rect.width);
        const pctY = getPercentageValue(topPos, rect.height);

        // Check if the tag is too close to another tag
        const isTagTooCloseToOthers = tags?.some((tag) => {
            const { positionX, positionY } = tag;
            const tooClosePx = 2;
            return (
                Math.abs(positionX - pctX) < tooClosePx &&
                Math.abs(positionY - pctY) < tooClosePx
            );
        });
        if (isTagTooCloseToOthers) {
            toast(
                'It is too close to another tag, please click on a different area or delete the existing tag first',
            );
            dispatch({
                type: 'ADDING_TAG',
                payload: undefined,
            });
            return;
        }

        dispatch({
            type: 'ADDING_TAG',
            payload: {
                pctX: roundToTwo(pctX),
                pctY: roundToTwo(pctY),
                rectWidth: rect.width,
                rectHeight: rect.height,
                libraryImageID: libraryImage.ID,
            },
        });
    };

    const listProductTags: number[] =
        tags
            ?.filter((tag) => tag?.product?.id)
            .map((tag) => (tag.product as TagProductType).id) || [];

    const { deleteTag } = useDeleteTag(libraryImage.ID, listProductTags);
    const { createTag } = useCreateTag(canDeleteTag);

    // Optimistic tags
    const { lastFetcher, addToFetcherQueue, clearFetcherQueue } =
        usePendingFetcherQueue<OptimisticTags>();

    const currentTags = lastFetcher?.tags ?? tags;

    const optimisticTags = useOptimisticTags(libraryImage.ID, currentTags);

    useEffect(() => {
        if (optimisticTags.fetcherKey) {
            addToFetcherQueue(optimisticTags);
        } else {
            clearFetcherQueue();
        }
    }, [addToFetcherQueue, clearFetcherQueue, optimisticTags]);

    const renderTags = () => {
        if (
            !optimisticTags.tags ||
            optimisticTags.tags.length === 0 ||
            !libraryImage
        )
            return null;

        const AspectRatio = libraryImage?.AspectRatio;
        const WidthBeforeCrop = libraryImage?.WidthBeforeCrop;
        const HeightBeforeCrop = libraryImage?.HeightBeforeCrop;
        const WidthAfterCrop = libraryImage?.WidthAfterCrop;
        const HeightAfterCrop = libraryImage?.HeightAfterCrop;
        const CropPosX = libraryImage?.CropPosX;
        const CropPosY = libraryImage?.CropPosY;

        if (
            AspectRatio === undefined ||
            WidthBeforeCrop === undefined ||
            HeightBeforeCrop === undefined ||
            HeightAfterCrop === undefined ||
            WidthAfterCrop === undefined ||
            CropPosX === undefined ||
            CropPosY === undefined
        ) {
            return null;
        }

        const boxHeight = ref.current?.clientHeight || 0;
        const boxWidth = ref.current?.clientWidth || 0;

        return optimisticTags.tags.map((tag) => {
            if (!tag && ref?.current) return null;

            const { positionX, positionY } = tag;

            // Get tag position
            const { pctAfterOffset } = getTagPosition(
                positionX,
                positionY,
                libraryImage,
                boxWidth,
                boxHeight,
            );

            return (
                <TagWithModals
                    key={tag.id}
                    marker={tag}
                    tagPosition={pctAfterOffset}
                    boxWidth={boxWidth}
                    showImageOverlay={tag.canApprove}
                    boxRef={ref.current as HTMLDivElement}
                    deleteTag={(id) => {
                        onTagDeleted?.(id);

                        return deleteTag(id);
                    }}
                    onTagUpdated={onTagCreatedOrUpdated}
                />
            );
        });
    };

    useClickAway(
        (event) => {
            const target = event.target as Element;
            if (tagPosition) {
                const closestParent = target.closest(
                    `.${PRODUCT_TAGGING_CLICKABLE_AREA_CLASS}`,
                );
                if (closestParent) {
                    return;
                }
                dispatch({
                    type: 'ADDING_TAG',
                    payload: undefined,
                });
            }
        },
        ref,
        ['mousedown', 'touchstart'],
    );

    return (
        <div
            className={`${css(
                S.ProductTaggingContainer,
            )} ${PRODUCT_TAGGING_CLICKABLE_AREA_CLASS}`}
            ref={ref}
        >
            {isTagging && (
                <ProductTaggingClickableArea onClick={onClickClickableArea} />
            )}
            {renderTags()}
            {isTagging && (
                <>
                    {tagPosition?.libraryImageID === libraryImage.ID && (
                        <ProductTaggingNewTag
                            tagPosition={tagPosition}
                            show={
                                tagPosition.libraryImageID === libraryImage.ID
                            }
                            boxRef={ref.current as HTMLDivElement}
                            onCreateMarker={(data) => {
                                createTag({
                                    data,
                                    tagPosition,
                                    libraryImage,
                                });
                                dispatch({
                                    type: 'ADDING_TAG',
                                    payload: undefined,
                                });
                                onTagCreatedOrUpdated?.();
                            }}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default ProductTaggingShell;
